/* eslint-disable react/no-multi-comp */
import React, { useState, type FunctionComponent, useEffect } from 'react'
import styled from '@emotion/styled'
import { Button, colors, Text } from 'ic-snacks'
import uniq from 'lodash/uniq'
import { Img } from 'react-image'
import { FormattedMessage } from 'react-intl'
import missingImageSrc from 'assets/missing-image.svg'
import Modal from 'legacy/components/Modal'
import Table from 'legacy/components/Table/Table'
import FlexRow from 'legacy/components/FlexRow'
import instacart from 'legacy/common/instacart'
import withRetailerRouter from 'legacy/common/withRouter'
import SearchBar from 'legacy/components/SearchBar'
import { withNotificationContext } from 'legacy/common/context'
import deprecatedAnalytics from 'legacy/common/deprecatedAnalytics'
import Dropdown from 'legacy/components/Dropdown'
import Divider from 'legacy/components/Divider'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import { type NotificationsContextValue } from 'legacy/contexts/notifications/NotificationsContext'
import { type ScopedRouteParams } from 'legacy/common/types'
import {
  type SearchProductsMeta,
  type SearchProduct,
} from 'legacy/sections/catalog/components/search/utils/catalogSearch.types'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { useCatalogSearchBehaviour } from 'legacy/sections/catalog/components/search/utils/catalogSearch.hooks'
import { type CatalogSearchType } from 'legacy/sections/catalog/components/search/utils/catalogSearch.constants'
import { LegacyRetailerLocaleSelect } from './departments/LegacyRetailerLocaleSelect'

const InnerBody = styled.div`
  align-items: left;
  justify-content: space-around;
`

const TitleContainer = styled.div`
  margin: 24px 37px 24px 37px;
`

const SearchContainer = styled(FlexRow)`
  margin: 24px 27px 24px 32px;
`

const FooterContainer = styled(FlexRow)`
  margin: 24px 37px 24px 37px;
`

const BottomDivider = styled(Divider)`
  margin-top: 14px;
`

const ImgContainer = styled.div`
  min-width: 40px;
  min-height: 40px;
  margin-right: 20px;
`

const SpaceHogger = styled.span`
  flex-grow: 1;
`

const searchTypeOptions = [
  { value: 'name', label: 'Name' },
  { value: 'upc', label: 'UPC' },
  { value: 'rrc', label: 'RRC' },
]

const SEARCH_TERM_MAX_LENGTH = 1500

const getTableProps = () => ({
  style: {
    background: colors.GRAY_93,
  },
})

const getTrProps = () => ({
  style: {
    background: 'white',
  },
})
interface Props extends RetailerRouteComponentProps<ScopedRouteParams>, NotificationsContextValue {
  aisleId: number
}

// Legacy search: per page is hardcoded to 10
const PER_PAGE = 10

const NewProductModal: FunctionComponent<Props> = ({
  history,
  match,
  aisleId,
  notify,
  notifyError,
}: Props) => {
  const { warehouse } = useWarehouseContext()
  const localeCode = warehouse.defaultLocale.localeCode.toUpperCase()

  const [searchInput, setSearchInput] = useState('')
  const [searchType, setSearchType] = useState<CatalogSearchType>('name')

  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [tableData, setTableData] = useState(null)
  const [meta, setMeta] = useState<SearchProductsMeta>({})

  const resultCount = meta.total_count

  const [showSearchTable, setShowSearchTable] = useState(false)

  const {
    searchResults,
    searchResultsLoading,
    searchResultsError,
    fetchSearchedProducts,
    clearSearchResults,
  } = useCatalogSearchBehaviour(true)

  const onClickAddToAisle = async (product, index) => {
    const { partner_id, warehouse_id } = match.params

    deprecatedAnalytics.track('product.add_to_aisle', {
      source_type: 'single',
      source_value: product.id,
    })

    try {
      await instacart.go.post(
        `/v1/partners/${partner_id}/warehouses/${warehouse_id}/products/add_to_aisle`,
        { data: { aisle_id: aisleId, product_id: product.id } }
      )

      notify(<FormattedMessage id="catalog.aisle.addProductRequested" />)

      const newTableData = [...tableData]
      newTableData[index].isRequested = true
      setTableData(newTableData)
    } catch (err) {
      notifyError(<FormattedMessage id="common.error" />)
    }
  }

  const renderNotInAisleButton = (original, index) =>
    original.isRequested ? (
      <Button snacksStyle="secondary" size="small" disabled>
        Requested
      </Button>
    ) : (
      <Button
        snacksStyle="primary"
        size="small"
        onClick={() => {
          onClickAddToAisle(original, index)
        }}
      >
        Add to Aisle
      </Button>
    )

  const isCurrentAisle = aisleIds => {
    return aisleIds && (aisleIds === aisleId || aisleIds.includes(aisleId))
  }

  const columns = () => [
    {
      header: 'Product name',
      Cell: ({ original }: { original: SearchProduct }) => (
        <FlexRow>
          <ImgContainer>
            <Img src={[original.image_url, missingImageSrc]} style={{ height: '70px' }} />
          </ImgContainer>
          <Text variant="T.14" style={{ whiteSpace: 'nowrap' }}>
            {original.display_name}
          </Text>
        </FlexRow>
      ),
    },
    {
      id: 'codes',
      header: 'Product codes',
      width: 200,
      Cell: ({ original }: { original: SearchProduct }) => (
        <div>
          {uniq(original.product_codes).map(code => (
            <Text elementType="div" variant="T.14" key={code}>
              {code}
            </Text>
          ))}
        </div>
      ),
    },
    {
      header: '',
      width: 140,
      Cell: ({ original, index }: { original: SearchProduct; index: number }) => {
        return isCurrentAisle(original.aisles && original.aisles.map(({ id }) => id)) ? (
          <Button snacksStyle="secondary" size="small" disabled>
            In Aisle
          </Button>
        ) : (
          renderNotInAisleButton(original, index)
        )
      },
    },
  ]

  const onClose = () => {
    history.goBack()
  }

  const onClickBulkAddToAisle = () => {
    const { partner_id, warehouse_id } = match.params

    deprecatedAnalytics.track('product.add_to_aisle', {
      source_type: 'all',
      source_value: searchInput,
    })

    instacart.go.post(
      `/v1/partners/${partner_id}/warehouses/${warehouse_id}/products/bulk_add_to_aisle`,
      {
        data: {
          aisle_id: aisleId,
          term: searchInput,
          searchType: searchType,
          locale_code: localeCode,
        },
      }
    )
    notify(`Request to add ${resultCount} products to this aisle submitted.`)
    onClose()
  }

  const onSearchInputChange = e => setSearchInput(e.target.value)

  const onSearch = async (e, opts: { page?: number } = {}) => {
    clearSearchResults()

    if (!searchInput) {
      return
    }
    setShowSearchTable(true)
    setIsLoading(true)
    setIsError(false)

    deprecatedAnalytics.track('catalog.aisle_product_search', {
      source_type: searchType,
      source_value: searchInput,
    })

    const page = opts.page ? opts.page : 1
    const offset = (page - 1) * PER_PAGE

    fetchSearchedProducts({
      limit: PER_PAGE,
      offset,
      filters: {
        searchType,
        searchTerm: searchInput,
      },
    })
  }

  // Handle mesh search result updates
  useEffect(() => {
    if (searchResultsError || searchResultsLoading) {
      return
    }

    const { data, meta: apiMeta } = searchResults

    const meta = apiMeta.total_count
      ? {
          total_count: apiMeta.total_count,
          per_page: PER_PAGE,
          total_pages: Math.ceil(apiMeta.total_count / PER_PAGE),
        }
      : {}

    setTableData(data)
    setMeta(meta)
    setIsLoading(false)
  }, [searchResults, searchResultsLoading, searchResultsError])

  useEffect(() => {
    setIsError(!!searchResultsError)
  }, [searchResultsError])

  useEffect(() => {
    setIsLoading(!!searchResultsLoading)
  }, [searchResultsLoading])

  const onPageChange = page => {
    onSearch(null, {
      page,
    })
  }

  const getPlaceholderText = () => {
    switch (searchType) {
      case 'name':
        return 'Search by product name'
      case 'upc':
        return 'Search by individual or bulk codes (e.g. 00039978021540, 00039978009524)'
      default:
        return 'Search by individual or bulk codes (e.g. 00091993, 150124, 120450)'
    }
  }

  return (
    <Modal width={900} height={'100%'} onClose={onClose}>
      {(Head, ModalBody) => [
        null,
        <ModalBody>
          <InnerBody
            css={
              !showSearchTable && {
                height: 200,
              }
            }
          >
            {showSearchTable && (
              <TitleContainer>
                <Text variant="T.18">Add Products</Text>
              </TitleContainer>
            )}
            <Divider />

            <SearchContainer>
              <SearchBar
                value={searchInput}
                onChange={onSearchInputChange}
                placeholder={getPlaceholderText()}
                onSearch={onSearch}
              />
              <Dropdown
                width="130px"
                options={searchTypeOptions}
                value={searchType}
                onChange={value => setSearchType(value)}
              />
              <div css={{ width: 150 }}>
                <LegacyRetailerLocaleSelect />
              </div>
            </SearchContainer>

            {showSearchTable && (
              <React.Fragment>
                <Table
                  isLoading={isLoading}
                  isError={isError}
                  data={tableData}
                  meta={meta}
                  columns={columns()}
                  getTableProps={getTableProps}
                  getTrProps={getTrProps}
                  onPageChange={onPageChange}
                  noDataText="No Products"
                  style={{
                    height: 'calc(100vh - 188px)',
                    width: '100%',
                    border: 'none',
                  }}
                />
                <BottomDivider />
                <FooterContainer>
                  <SpaceHogger />
                  {resultCount && resultCount > 1 && (
                    <Button snacksStyle="primary" size="small" onClick={onClickBulkAddToAisle}>
                      {`Add all (${resultCount})`}
                    </Button>
                  )}
                </FooterContainer>
              </React.Fragment>
            )}
          </InnerBody>
        </ModalBody>,
        null,
      ]}
    </Modal>
  )
}

export default withRetailerRouter(withNotificationContext(NewProductModal))
